import { getCookieValue, getRootDomainForCookie } from './helpers';

const isProduction = process.env.NEXT_PUBLIC_HOME_REMOTE_CONFIG_ENV === 'PROD';

const ATTR_SYNC_FLAG = 'attributionSyncing';
const UTM_SUCCESS_COOKIE = 'isUTMMutationHitSuccess';

// Mapping from URL param -> mutation/cookie key
const UTM_PARAM_TO_KEY = {
  utm_source: 'utmSource',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_term: 'utmTerm',
  utm_medium: 'utmMedium',
  gclid: 'gcl_id',
};

const DEFAULT_KEYS = Object.values(UTM_PARAM_TO_KEY);

function isEligibleHost(hostname) {
  const host = String(hostname || '').toLowerCase();
  return host.endsWith('.nextias.com') || host.endsWith('.madeeasyprime.com') || host.endsWith('.madeeasy.in');
}

function normalizeValue(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

function setCookie({
  name,
  value,
  maxAgeSeconds = 60 * 60 * 24 * 30,
}) {
  if (!isProduction) return;
  const safeValue = normalizeValue(value);
  if (!safeValue) return;

  const domain = getRootDomainForCookie();
  const isHttps = typeof window !== 'undefined' && window.location?.protocol === 'https:';
  const sameSite = isHttps ? 'None' : 'Lax';
  const secure = isHttps ? '; Secure' : '';

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(safeValue)}; Path=/; Domain=${domain}; Max-Age=${maxAgeSeconds}; SameSite=${sameSite}${secure}`;
}

export function readAttributionCookies(keys = DEFAULT_KEYS) {
  const out = {};
  keys.forEach((k) => {
    const val = getCookieValue(k);
    if (normalizeValue(val)) out[k] = val;
  });
  return out;
}

export function persistAttributionFromUrlSearch({
  hostname,
  search,
  keys = DEFAULT_KEYS,
}) {
  if (typeof window === 'undefined') return { persisted: {}, eligible: false };
  if (!isProduction) return { persisted: {}, eligible: false };
  if (!isEligibleHost(hostname || window.location.hostname)) return { persisted: {}, eligible: false };

  const params = new URLSearchParams(String(search || window.location.search || ''));
  const existing = readAttributionCookies(keys);

  const next = {};
  Object.entries(UTM_PARAM_TO_KEY).forEach(([paramKey, cookieKey]) => {
    if (!keys.includes(cookieKey)) return;
    const v = normalizeValue(params.get(paramKey));
    if (v) {
      next[cookieKey] = v;
    }
  });

  // If no UTM values in URL, do nothing
  if (Object.keys(next).length === 0) {
    return { persisted: {}, eligible: true };
  }

  // Only save if at least one key is different from existing cookie values
  const hasChange = Object.keys(next).some((k) => next[k] !== existing[k]);
  if (!hasChange) {
    return { persisted: {}, eligible: true };
  }

  // New or changed UTM set: reset cookies to 30 days and clear success flag
  Object.entries(next).forEach(([cookieKey, value]) => {
    setCookie({ name: cookieKey, value, maxAgeSeconds: 60 * 60 * 24 * 30 });
  });

  // Clear success flag so that next login can trigger mutation with new values
  setCookie({ name: UTM_SUCCESS_COOKIE, value: 'false', maxAgeSeconds: 60 * 60 * 24 * 30 });

  return { persisted: next, eligible: true };
}

export function shouldSyncAttribution() {
  if (typeof window === 'undefined') return false;
  return !window.sessionStorage.getItem(ATTR_SYNC_FLAG);
}

export function markAttributionSynced() {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(ATTR_SYNC_FLAG, '1');
}

export function isAttributionEligibleHost(hostname) {
  return isEligibleHost(hostname);
}

export function hasUtmMutationSucceeded() {
  const val = getCookieValue(UTM_SUCCESS_COOKIE);
  return val === 'true';
}

export function markUtmMutationSucceeded(keys = DEFAULT_KEYS) {
  // Mark success and shrink TTL of existing UTM cookies to 7 days
  setCookie({ name: UTM_SUCCESS_COOKIE, value: 'true', maxAgeSeconds: 60 * 60 * 24 * 7 });

  const current = readAttributionCookies(keys);
  if (!current || Object.keys(current).length === 0) return;

  Object.entries(current).forEach(([k, v]) => {
    setCookie({ name: k, value: v, maxAgeSeconds: 60 * 60 * 24 * 7 });
  });
}

export function buildUtmMutationPayload() {
  const data = readAttributionCookies();
  if (!data || Object.keys(data).length === 0) return null;
  const payload = {
    utmCampaign: data.utmCampaign || null,
    utmContent: data.utmContent || null,
    utmMedium: data.utmMedium || null,
    utmSource: data.utmSource || null,
    utmTerm: data.utmTerm || null,
    gclid: data.gcl_id || null,
  };

  const hasAnyValue = Object.values(payload).some((v) => normalizeValue(v));
  if (!hasAnyValue) return null;

  return payload;
}
